import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Send, Check, X, Calendar, DollarSign, Tag, Clock, Star, CreditCard } from "lucide-react";
import { api, resolveFileUrl } from "../api";
import { useAuth } from "../contexts";
import { PageHeader, EmptyState } from "../components/ui-bits";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending:   "chip",
  accepted:  "chip chip-accent",
  declined:  "chip",
  completed: "chip chip-accent",
  cancelled: "chip",
};

export default function BookingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("received");
  const [data, setData] = useState({ sent: [], received: [] });
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const load = () => {
    if (!user) return;
    setBusy(true);
    api.get("/bookings/mine").then((r) => setData(r.data)).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [user]);

  if (!user) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
        <p className="font-mono-label text-neutral-500 mb-4">Sign in to see your bookings.</p>
        <Link to="/login" className="btn-primary">Sign In</Link>
      </div>
    );
  }

  const items = data[tab] || [];

  const act = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const payBooking = async (id) => {
    try {
      const fd = new FormData();
      fd.append("origin_url", window.location.origin);
      const r = await api.post(`/bookings/${id}/checkout`, fd);
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };

  return (
    <div className="pb-32" data-testid="bookings-page">
      <PageHeader kicker="THE INBOX" title="BOOKINGS" sub="Manage incoming requests and track the gigs, features, and sessions you've sent out." />
      <div className="divider-tape" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-1 border-b border-neutral-800 mb-6 overflow-x-auto" data-testid="bookings-tabs">
          {[
            { key: "received", label: "Received", icon: Inbox, count: data.received.length },
            { key: "sent",     label: "Sent",     icon: Send,  count: data.sent.length },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`font-mono-label text-[11px] px-4 py-3 border-b-2 inline-flex items-center gap-2 transition-colors ${
                tab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-neutral-400 hover:text-white"
              }`}
              data-testid={`bookings-tab-${t.key}`}
            >
              <t.icon size={12} /> {t.label.toUpperCase()} <span className="text-neutral-500">{t.count}</span>
            </button>
          ))}
        </div>

        {busy ? (
          <p className="font-mono-label text-[10px] text-neutral-500">Loading…</p>
        ) : items.length === 0 ? (
          <EmptyState>
            {tab === "received" ? "No incoming booking requests yet." : "You haven't booked anyone yet — try Culture Connect."}
          </EmptyState>
        ) : (
          <div className="space-y-4">
            {items.map((b) => (
              <BookingCard key={b.id} booking={b} tab={tab} onAct={act} onReview={(bk) => setReviewing(bk)} onPay={payBooking} />
            ))}
          </div>
        )}
      </div>
      {reviewing && (
        <ReviewModal
          booking={reviewing}
          onClose={(refresh) => { setReviewing(null); if (refresh) load(); }}
        />
      )}
    </div>
  );
}

function ReviewModal({ booking, onClose }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const target = booking.other_user || {};
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/reviews", {
        target_id: booking.artist_id || target.id,
        booking_id: booking.id,
        rating: Number(rating),
        comment,
      });
      booking.reviewed = true;
      onClose(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't submit review");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-4" onClick={() => onClose(false)} data-testid="review-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-headline text-xl uppercase">Leave a Review</h3>
          <button onClick={() => onClose(false)}><X size={18} /></button>
        </div>
        <div className="font-mono-label text-[10px] text-neutral-500 mb-4">FOR {(target.display_name || target.username || "this booking").toUpperCase()}</div>
        <div className="mb-4">
          <div className="font-mono-label text-[10px] text-neutral-400 mb-2">RATING</div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)} className="p-1" data-testid={`review-star-${n}`}>
                <Star size={26} className={n <= rating ? "text-[var(--accent)] fill-[var(--accent)]" : "text-neutral-700"} />
              </button>
            ))}
          </div>
        </div>
        <textarea rows={4} className="input-brut resize-none mb-3" placeholder="Tell other clients what working with them was like (optional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
        {err && <p className="text-[var(--accent)] font-mono-label text-[10px] mb-2" data-testid="review-error">{err}</p>}
        <div className="flex gap-2">
          <button onClick={submit} disabled={busy} className="btn-primary flex-1 justify-center" data-testid="review-submit">{busy ? "Posting…" : "Post Review"}</button>
          <button onClick={() => onClose(false)} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function BookingCard({ booking, tab, onAct, onReview, onPay }) {
  const status = booking.status || "pending";
  const other = booking.other_user || {};
  const isReceived = tab === "received";
  return (
    <div className="border border-neutral-800 p-5" data-testid={`booking-${booking.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          {other.avatar ? (
            <img src={resolveFileUrl(other.avatar)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />
          ) : (
            <div className="w-10 h-10 border border-neutral-800 bg-neutral-900 flex items-center justify-center font-mono-label text-[10px]">
              {(other.display_name || other.username || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-headline text-sm uppercase truncate">
              {isReceived ? "FROM" : "TO"} · {other.display_name || other.username || "Unknown"}
            </div>
            <div className="font-mono-label text-[10px] text-neutral-500">
              {other.username && <Link to={`/artist/${other.username}`} className="hover:text-white">@{other.username}</Link>}
            </div>
          </div>
        </div>
        <span className={STATUS_STYLES[status] || "chip"} data-testid={`booking-status-${booking.id}`}>
          {status.toUpperCase()}
        </span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 font-mono-label text-[10px] text-neutral-400">
        <Field icon={Calendar} label="DATE" value={booking.event_date} />
        <Field icon={Tag} label="TYPE" value={booking.event_type?.toUpperCase()} />
        <Field icon={DollarSign} label="BUDGET" value={`$${Number(booking.budget || 0).toFixed(0)}`} />
        <Field icon={Clock} label="SENT" value={new Date(booking.created_at).toLocaleDateString()} />
      </div>

      {booking.payment_status === "paid" && (
        <div className="border border-[var(--accent)] bg-[#1a0a05] p-3 mb-4 font-mono-label text-[10px] text-[var(--accent)]" data-testid={`pay-breakdown-${booking.id}`}>
          PAYMENT ${Number(booking.budget || 0).toFixed(2)} · PLATFORM FEE ({Number(booking.platform_fee_pct || 0).toFixed(1)}%) ${Number(booking.platform_fee || 0).toFixed(2)} · {isReceived ? "YOUR NET" : "CREATOR NET"} <span className="text-white">${Number(booking.artist_net || 0).toFixed(2)}</span>{booking.payout_released && " · RELEASED"}
        </div>
      )}

      {booking.details && <p className="text-neutral-300 text-sm mb-4 whitespace-pre-line border-l-2 border-neutral-800 pl-3">{booking.details}</p>}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-800">
        {isReceived && status === "pending" && (
          <>
            <button onClick={() => onAct(booking.id, "accepted")} className="btn-primary !py-2 !px-3" data-testid={`accept-${booking.id}`}>
              <Check size={12} /> Accept
            </button>
            <button onClick={() => onAct(booking.id, "declined")} className="btn-ghost !py-2 !px-3" data-testid={`decline-${booking.id}`}>
              <X size={12} /> Decline
            </button>
          </>
        )}
        {!isReceived && status === "accepted" && booking.payment_status !== "paid" && (
          <button onClick={() => onPay && onPay(booking.id)} className="btn-primary !py-2 !px-3" data-testid={`pay-${booking.id}`}>
            <CreditCard size={12} /> Pay ${Number(booking.budget || 0).toFixed(0)}
          </button>
        )}
        {booking.payment_status === "paid" && status !== "completed" && (
          <span className="chip chip-accent inline-flex items-center gap-1" data-testid={`paid-${booking.id}`}>
            <Check size={10} /> PAID · HELD UNTIL COMPLETE
          </span>
        )}
        {!isReceived && status === "accepted" && booking.payment_status === "paid" && (
          <button onClick={() => onAct(booking.id, "completed")} className="btn-primary !py-2 !px-3" data-testid={`complete-${booking.id}`}>
            <Check size={12} /> Mark Complete · Release Payout
          </button>
        )}
        {!isReceived && status === "accepted" && booking.payment_status !== "paid" && (
          <button onClick={() => onAct(booking.id, "completed")} className="btn-ghost !py-2 !px-3 text-[10px]" data-testid={`complete-unpaid-${booking.id}`}>
            Mark Complete (skip payment)
          </button>
        )}
        {!isReceived && status === "completed" && !booking.reviewed && (
          <button onClick={() => onReview && onReview(booking)} className="btn-primary !py-2 !px-3" data-testid={`review-${booking.id}`}>
            <Star size={12} /> Leave Review
          </button>
        )}
        {!isReceived && status === "completed" && booking.reviewed && (
          <span className="chip chip-accent inline-flex items-center gap-1" data-testid={`reviewed-${booking.id}`}><Star size={10} className="fill-[var(--accent)]" /> REVIEWED</span>
        )}
        {(status === "pending" || status === "accepted") && (
          <button onClick={() => onAct(booking.id, "cancelled")} className="btn-ghost !py-2 !px-3" data-testid={`cancel-${booking.id}`}>
            <X size={12} /> Cancel
          </button>
        )}
        {other.username && (
          <Link to={`/messages?to=${other.username}`} className="btn-ghost !py-2 !px-3 ml-auto" data-testid={`message-${booking.id}`}>
            Message
          </Link>
        )}
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={12} className="text-neutral-500" />
      <span className="text-neutral-500">{label}</span>
      <span className="text-white truncate">{value}</span>
    </div>
  );
}
